import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { showToast } from '@/shared/ui/toast';

import type { ClinicalRecordPrintPatient } from './clinical-record-print';
import {
  CLINICAL_LAB_REQUESTS_QUERY,
  CLINICAL_LAB_RESULTS_QUERY,
  CREATE_LAB_REQUEST_MUTATION,
  RECORD_LAB_RESULT_MUTATION,
  UPDATE_LAB_REQUEST_STATUS_MUTATION,
  idempotencyContext,
} from './clinical.gql';
import { printLabOrder } from './lab-order-print';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call */

const LAB_STATUSES = ['pending', 'sent', 'partial_results', 'complete', 'cancelled'] as const;
const LAB_STATUS_VARIANT: Record<string, NonNullable<BadgeProps['variant']>> = {
  pending: 'muted',
  sent: 'default',
  partial_results: 'warning',
  complete: 'success',
  cancelled: 'destructive',
};

function errText(e: unknown): string {
  if (e instanceof ApolloError) return e.graphQLErrors[0]?.message ?? e.message;
  if (e instanceof Error) return e.message;
  return 'error';
}

function LabRequestCard({
  req,
  canWrite,
  appendable,
  onError,
}: {
  req: any;
  canWrite: boolean;
  appendable: boolean;
  onError: (m: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery(CLINICAL_LAB_RESULTS_QUERY, {
    variables: { lab_request_id: req.id },
    fetchPolicy: 'cache-and-network',
  });
  const results: any[] = data?.clinicalLabResults ?? [];
  const [updateStatus] = useMutation(UPDATE_LAB_REQUEST_STATUS_MUTATION);
  const [recordResult] = useMutation(RECORD_LAB_RESULT_MUTATION);

  const [form, setForm] = useState({ test_name: '', result_value: '', unit: '', reference_range: '', is_abnormal: false, interpretation: '' });

  const onStatus = async (status: string): Promise<void> => {
    try {
      await updateStatus({ variables: { input: { lab_request_id: req.id, status } }, context: idempotencyContext() });
    } catch (e) {
      onError(errText(e));
    }
  };

  const onAddResult = async (): Promise<void> => {
    if (form.test_name.trim() === '') return;
    try {
      await recordResult({
        variables: {
          input: {
            lab_request_id: req.id,
            test_name: form.test_name.trim(),
            result_value: form.result_value.trim() || undefined,
            unit: form.unit.trim() || undefined,
            reference_range: form.reference_range.trim() || undefined,
            is_abnormal: form.is_abnormal,
            interpretation: form.interpretation.trim() || undefined,
          },
        },
        context: idempotencyContext(),
      });
      setForm({ test_name: '', result_value: '', unit: '', reference_range: '', is_abnormal: false, interpretation: '' });
      await refetch();
      showToast({ tone: 'success', title: t('clinical.toast_lab_result_recorded') });
    } catch (e) {
      onError(errText(e));
    }
  };

  return (
    <div className="space-y-2 rounded-md border p-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">
          {req.panel_name || t('clinical.lab_request')}
          {req.lab_name ? <span className="text-muted-foreground"> · {req.lab_name}</span> : null}
        </p>
        {canWrite && appendable ? (
          <select
            className="border-input bg-background rounded-md border px-2 py-1 text-xs"
            value={req.status}
            onChange={(e) => void onStatus(e.target.value)}
          >
            {LAB_STATUSES.map((st) => (
              <option key={st} value={st}>
                {t(`clinical.lab_status_${st}`)}
              </option>
            ))}
          </select>
        ) : (
          <Badge variant={LAB_STATUS_VARIANT[req.status] ?? 'muted'}>
            {t(`clinical.lab_status_${req.status}`)}
          </Badge>
        )}
      </div>

      {results.length > 0 ? (
        <ul className="space-y-1 text-sm">
          {results.map((r) => (
            <li key={r.id} className="flex justify-between gap-2">
              <span>
                {r.is_abnormal ? <span className="text-destructive">⚠ </span> : null}
                <strong>{r.test_name}</strong>: {r.result_value ?? '—'} {r.unit ?? ''}
                {r.reference_range ? (
                  <span className="text-muted-foreground"> ({r.reference_range})</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground text-xs">{t('clinical.no_lab_results')}</p>
      )}

      {canWrite && appendable ? (
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder={t('clinical.lab_test_name')} value={form.test_name} onChange={(e) => setForm({ ...form, test_name: e.target.value })} />
          <Input placeholder={t('clinical.lab_value')} value={form.result_value} onChange={(e) => setForm({ ...form, result_value: e.target.value })} />
          <Input placeholder={t('clinical.lab_unit')} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
          <Input placeholder={t('clinical.lab_reference')} value={form.reference_range} onChange={(e) => setForm({ ...form, reference_range: e.target.value })} />
          <label className="col-span-2 flex items-center gap-1 text-sm">
            <input type="checkbox" checked={form.is_abnormal} onChange={(e) => setForm({ ...form, is_abnormal: e.target.checked })} />
            {t('clinical.lab_abnormal')}
          </label>
          <div className="col-span-2 flex justify-end">
            <Button size="sm" onClick={() => void onAddResult()} disabled={form.test_name.trim() === ''}>
              {t('clinical.add_lab_result')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function LabsSection({
  visitId,
  canWrite,
  editable,
  appendable,
  printBase,
  chiefComplaint,
  primaryDiagnosis,
}: {
  visitId: string;
  canWrite: boolean;
  editable: boolean;
  appendable: boolean;
  printBase: ClinicalRecordPrintPatient;
  chiefComplaint?: string | null;
  primaryDiagnosis?: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery(CLINICAL_LAB_REQUESTS_QUERY, {
    variables: { visit_id: visitId },
    fetchPolicy: 'cache-and-network',
  });
  const requests: any[] = data?.clinicalLabRequests ?? [];
  const [createReq] = useMutation(CREATE_LAB_REQUEST_MUTATION);

  const [order, setOrder] = useState({ lab_name: '', panel_name: '', notes: '' });
  const [error, setError] = useState<string | null>(null);

  const onDownloadOrder = async (): Promise<void> => {
    const ok = await printLabOrder(
      {
        patient: printBase,
        chief_complaint: chiefComplaint,
        primary_diagnosis: primaryDiagnosis,
        requests,
      },
      t,
    );
    if (!ok) showToast({ tone: 'error', title: t('clinical.record_print_blocked') });
  };

  const onOrder = async (): Promise<void> => {
    if (order.panel_name.trim() === '' && order.lab_name.trim() === '') return;
    setError(null);
    try {
      await createReq({
        variables: {
          input: {
            clinical_visit_id: visitId,
            lab_name: order.lab_name.trim() || undefined,
            panel_name: order.panel_name.trim() || undefined,
            notes: order.notes.trim() || undefined,
          },
        },
        context: idempotencyContext(),
      });
      setOrder({ lab_name: '', panel_name: '', notes: '' });
      await refetch();
      showToast({ tone: 'success', title: t('clinical.toast_lab_request_created') });
    } catch (e) {
      setError(errText(e));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={() => void onDownloadOrder()}>
          <Download className="h-4 w-4" aria-hidden />
          <span>{t('clinical.download_lab_order')}</span>
        </Button>
      </div>

      {error ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
          {error}
        </div>
      ) : null}

      {requests.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('clinical.no_lab_requests')}</p>
      ) : (
        <div className="space-y-2">
          {requests.map((req) => (
            <LabRequestCard key={req.id} req={req} canWrite={canWrite} appendable={appendable} onError={setError} />
          ))}
        </div>
      )}

      {canWrite && editable ? (
        <div className="space-y-2 rounded-md border p-2">
          <div className="space-y-1">
            <Label>{t('clinical.lab_panel')}</Label>
            <Input value={order.panel_name} onChange={(e) => setOrder({ ...order, panel_name: e.target.value })} placeholder={t('clinical.lab_panel_placeholder')} />
          </div>
          <div className="space-y-1">
            <Label>{t('clinical.lab_external_name')}</Label>
            <Input value={order.lab_name} onChange={(e) => setOrder({ ...order, lab_name: e.target.value })} />
          </div>
          <Button size="sm" onClick={() => void onOrder()}>
            {t('clinical.order_lab')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
